import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import type { ReportTypeType } from '@coursera/event-pulse-types';

import { type ReportAction, type ReportType, type ReportV2 } from 'bundles/enterprise-admin-analytics/types';

import _t from 'i18n!nls/enterprise-admin-analytics';

export const getLearnerActivityProgressReport = (enableLearnerGroups?: boolean): ReportV2 => ({
  reportType: 'LEARNER_ENGAGEMENT',
  name: _t('Learner activity & progress'),
  description: enableLearnerGroups
    ? _t('Offers a comprehensive view of learner engagement across groups and content types.')
    : _t('Offers a comprehensive view of learner engagement across programs and content types.'),
  descriptionPoints: [
    _t('Offers a comprehensive view of learner engagement'),
    enableLearnerGroups
      ? _t('Covers multiple groups and content types')
      : _t('Covers multiple programs and content types'),
    <React.Fragment key="includes-content">
      {_t('Includes ')}
      <Typography2 component="span" variant="subtitleMedium">
        {_t('Learning Paths')}
      </Typography2>
      {_t(', Specializations, and Courses')}
    </React.Fragment>,
  ],
  previouslyKnownAs: _t('Usage Report, Membership Report, or Content Activity Report'),
});

export const getSkillDevelopmentReport = (enableLearnerGroups?: boolean): ReportV2 => ({
  reportType: 'LEARNER_SKILLS',
  name: _t('Skill development'),
  description: enableLearnerGroups
    ? _t('Shows learner skill growth across groups with industry benchmarking.')
    : _t('Shows learner skill growth across programs with industry benchmarking.'),
  descriptionPoints: [
    enableLearnerGroups
      ? _t('Show learner skill growth for tracked skills across groups')
      : _t('Show learner skill growth for tracked skills across programs'),
    _t('Breaks down growth by content type'),
    _t('Includes industry benchmarking for comparison'),
    <React.Fragment key="contains-reports">
      {_t('Contains reports like ')}
      <Typography2 component="span" variant="subtitleMedium">
        {_t('Skill Breakdown, Leaderboard & Benchmarking')}
      </Typography2>
    </React.Fragment>,
  ],
  isNew: true,
});

export const getLearnerPerformanceReport = (): ReportV2 => ({
  reportType: 'LEARNER_PERFORMANCE',
  name: _t('Learner performance'),
  description: _t('Displays learner grades and highlights learners flagged for plagiarism.'),
  descriptionPoints: [
    _t('Displays learner grades'),
    _t('Highlights learners flagged for plagiarism'),
    _t('Provides an overview of academic performance and integrity'),
  ],
  previouslyKnownAs: _t('Gradebook Report and Plagiarism Report'),
});

export const getCurriculumReport = (): ReportV2 => ({
  reportType: 'CURRICULUM',
  name: _t('Curriculum'),
  description: _t('Lists courses and specializations in curated collections with item details and course links.'),
  descriptionPoints: [
    _t('Lists Courses and Specializations in curated collections'),
    _t('Includes detailed item information'),
    _t('Provides direct course links'),
  ],
});

export const getMonthlyDigestReport = (): ReportV2 => ({
  reportType: 'MONTHLY_DIGEST_EMAIL',
  name: _t('Monthly digest'),
  description: _t("Regular updates on your organization's learners and their engagement on Coursera."),
  descriptionPoints: [
    _t('Provides monthly updates on license utilization'),
    _t('Highlights learner activity and engagement'),
    _t('Summarizes organization-wide performance on Coursera'),
  ],
});

export const getVspProgressReport = (): ReportV2 => ({
  reportType: 'VERIFIED_SKILL_PATH_PROGRESS',
  name: _t('Verified Skill Paths Progress Report'),
  description: _t(
    'Download learner progress for those enrolled in Verified Skill Paths, including skills mastered and assessment activity within each path.'
  ),
  descriptionPoints: [
    _t('Download learner progress for those enrolled in Verified Skill Paths'),
    _t('Includes skills mastered and assessment activity within each path'),
  ],
  isNew: true,
});

export const getInteractiveItemsAndLabsReport = (): ReportV2 => ({
  reportType: 'INTERACTIVE_ITEMS_AND_LABS',
  name: _t('Interactive Items & Labs'),
  description: _t(
    'Shows engagement and performance for Role Play, Dialogue, Programming Assignments, and Ungraded Labs.'
  ),
  descriptionPoints: [
    _t('Shows engagement and performance for Role Play, Dialogue, Programming Assignments, and Ungraded Labs'),
    _t('Covers item engagement, sessions, duration, feedback, and grades (where applicable)'),
    <React.Fragment key="contains-reports">
      {_t('Contains reports for ')}
      <Typography2 component="span" variant="subtitleMedium">
        {_t('Role Play & Dialogue')}
      </Typography2>
      {_t(' (Aggregate and Learner Level) and ')}
      <Typography2 component="span" variant="subtitleMedium">
        {_t('Programming Assignments & Ungraded Labs')}
      </Typography2>
      {_t(' (Aggregate and Learner Level)')}
    </React.Fragment>,
  ],
  isNew: true,
});

export const getAllReportDefinitions = (enableLearnerGroups?: boolean): ReportV2[] => [
  getLearnerActivityProgressReport(enableLearnerGroups),
  getSkillDevelopmentReport(enableLearnerGroups),
  getVspProgressReport(),
  getLearnerPerformanceReport(),
  getInteractiveItemsAndLabsReport(),
  getCurriculumReport(),
  getMonthlyDigestReport(),
];

// Function to avoid calling _t() at module load time (SSR-safe)
export const getReportDefinitionsMap = (enableLearnerGroups?: boolean): Record<ReportType, ReportV2> => ({
  LEARNER_ENGAGEMENT: getLearnerActivityProgressReport(enableLearnerGroups),
  LEARNER_SKILLS: getSkillDevelopmentReport(enableLearnerGroups),
  LEARNER_PERFORMANCE: getLearnerPerformanceReport(),
  CURRICULUM: getCurriculumReport(),
  MONTHLY_DIGEST_EMAIL: getMonthlyDigestReport(),
  VERIFIED_SKILL_PATH_PROGRESS: getVspProgressReport(),
  INTERACTIVE_ITEMS_AND_LABS: getInteractiveItemsAndLabsReport(),
});

export const REPORT_ACTIONS_FOR_URL = {
  PREVIEW_REPORT: 'preview',
  EDIT_SCHEDULED_REPORT: 'edit',
  CONFIGURE_REPORT: 'configure',
  CANCEL_SCHEDULED_REPORT: 'cancel',
  SCHEDULE_MONTHLY_DIGEST_REPORT: 'schedule-monthly-digest',
} satisfies Record<ReportAction, string>;

export const REPORT_TYPE_FOR_URL: Record<ReportType, string> = {
  LEARNER_ENGAGEMENT: 'learner-engagement',
  LEARNER_SKILLS: 'learner-skills',
  LEARNER_PERFORMANCE: 'learner-performance',
  CURRICULUM: 'curriculum',
  MONTHLY_DIGEST_EMAIL: 'monthly-digest',
  VERIFIED_SKILL_PATH_PROGRESS: 'verified-skill-path-progress',
  INTERACTIVE_ITEMS_AND_LABS: 'interactive-items-and-labs',
};

export const REPORT_TYPE_IN_CAMEL_CASE: Record<ReportType, string> = {
  LEARNER_ENGAGEMENT: 'learnerEngagement',
  LEARNER_SKILLS: 'learnerSkills',
  LEARNER_PERFORMANCE: 'learnerPerformance',
  CURRICULUM: 'curriculum',
  MONTHLY_DIGEST_EMAIL: 'monthlyDigestEmail',
  VERIFIED_SKILL_PATH_PROGRESS: 'verifiedSkillPathProgress',
  INTERACTIVE_ITEMS_AND_LABS: 'interactiveItemsAndLabs',
};

export const REPORT_TYPE_FOR_TRACKING: Record<ReportType, ReportTypeType> = {
  LEARNER_ENGAGEMENT: 'learner_activity_and_progress',
  LEARNER_SKILLS: 'skill_development',
  LEARNER_PERFORMANCE: 'learner_performance',
  CURRICULUM: 'curriculum',
  MONTHLY_DIGEST_EMAIL: 'monthly_digest',
  VERIFIED_SKILL_PATH_PROGRESS: 'verified_skill_path_progress' as ReportTypeType,
  INTERACTIVE_ITEMS_AND_LABS: 'interactive_items_and_labs' as ReportTypeType,
};

/**
 * Polling configuration for report generation
 * Defines how often to poll and when to timeout for each report type
 */
export type ReportPollingConfig = {
  frequency: number; // Polling frequency in milliseconds
  timeout: number; // Timeout in milliseconds
};

/**
 * Report-specific polling configurations
 * Customize these values based on report complexity and expected generation time
 */
export const REPORT_POLLING_CONFIG: Record<Exclude<ReportType, 'MONTHLY_DIGEST_EMAIL'>, ReportPollingConfig> = {
  LEARNER_ENGAGEMENT: {
    frequency: 30 * 1000, // 30 seconds
    timeout: 4 * 60 * 60 * 1000, // 4 hours
  },
  LEARNER_SKILLS: {
    frequency: 30 * 1000, // 30 seconds
    timeout: 4 * 60 * 60 * 1000, // 4 hours
  },
  LEARNER_PERFORMANCE: {
    frequency: 30 * 1000, // 30 seconds
    timeout: 4 * 60 * 60 * 1000, // 4 hours
  },
  CURRICULUM: {
    frequency: 30 * 1000, // 30 seconds
    timeout: 4 * 60 * 60 * 1000, // 4 hours
  },
  VERIFIED_SKILL_PATH_PROGRESS: {
    frequency: 30 * 1000, // 30 seconds
    timeout: 4 * 60 * 60 * 1000, // 4 hours
  },
  INTERACTIVE_ITEMS_AND_LABS: {
    frequency: 30 * 1000, // 30 seconds
    timeout: 4 * 60 * 60 * 1000, // 4 hours
  },
};
